import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../services/firebase/config'
import { targetedCloneForMutation } from '@/utils/cloneHelpers'
import { useUserStore } from './userStore'
import { useCampaignStore } from './campaignStore'
import MediaService from '../services/media/MediaService'
import PermissionService, { PermissionLevel } from '../services/firebase/PermissionService'
import { v4 as uuidv4 } from 'uuid'
import StorageQuotaService from '../services/storage/StorageQuotaService'

/**
 * Store for managing campaign resources (images, maps, notes, POIs, etc.)
 * with support for permission-based access
 */
export const useCampaignResourceStore = defineStore('campaignResource', () => {
  // Dependencies
  const userStore = useUserStore()
  const campaignStore = useCampaignStore()

  // State
  const resources = ref([])
  const isLoading = ref(false)
  const error = ref(null)

  // Getters
  const getResourcesByType = computed(() => (type, campaignId) => {
    return resources.value.filter(
      (resource) => resource.resourceType === type && resource.campaignId === campaignId
    )
  })

  const getResourceById = computed(() => (resourceId) => {
    return resources.value.find((resource) => resource.id === resourceId)
  })

  const getAccessibleResources = computed(() => (campaignId) => {
    const userId = userStore.userId
    if (!userId) return []

    const campaign = campaignStore.getCampaigns.find((c) => c.id === campaignId)
    if (!campaign) return []

    return resources.value.filter((resource) => {
      // Check if user has access to this resource
      return PermissionService.canRead(resource.permission, userId, campaign)
    })
  })

  /**
   * Upload a resource with appropriate permissions
   * @param {string} resourceType - Type of resource (images, maps, notes, pois, etc)
   * @param {string} campaignId - Campaign ID
   * @param {File} file - File to upload
   * @param {string} permissionLevel - Permission level (public, campaign, private, specific)
   * @param {Array<string>} specificUsers - Array of user IDs for USER_SPECIFIC permission
   * @param {Object} metadata - Additional metadata for the resource
   * @returns {Promise<Object>} - Uploaded resource object
   */
  async function uploadResource(
    resourceType,
    campaignId,
    file,
    permissionLevel = PermissionLevel.CAMPAIGN,
    specificUsers = [],
    metadata = {}
  ) {
    if (!resourceType || !campaignId || !file) {
      throw new Error('Resource type, campaign ID, and file are required')
    }

    isLoading.value = true
    error.value = null

    try {
      // Get current user ID
      const userId = userStore.userId
      if (!userId) {
        throw new Error('User must be logged in to upload resources')
      }

      // Get campaign to verify permissions
      const campaign = await campaignStore.fetchCampaign(campaignId)
      if (!campaign) {
        throw new Error('Campaign not found')
      }

      // Verify user has permission to add resources to campaign
      if (campaign.dm !== userId && !userStore.isAdmin) {
        throw new Error('Only the DM or admin can add resources to this campaign')
      }

      // Create permission object for resource
      const permissionObject = PermissionService.createPermission(
        userId,
        permissionLevel,
        specificUsers,
        campaignId
      )

      // Determine resize options based on resource type
      const shouldResize = ['images', 'maps'].includes(resourceType)
      const fileExt = file.name.split('.').pop()
      const resourceId = uuidv4()
      const initialFileName = `${resourceId}.${fileExt}`
      const initialPath = PermissionService.getStoragePath(
        resourceType,
        campaignId,
        permissionLevel,
        initialFileName
      )
      const fileToUpload = shouldResize ? await MediaService.resizeImage(file, 1280, 720) : file

      const url = await MediaService.uploadManaged({
        storagePath: initialPath,
        file: fileToUpload,
        userId,
        resourceType: `campaign-${resourceType}`,
        makePublic: true
      })

      // Normalize returned values (may be converted to JPEG with a new path)
      let finalPath = initialPath
      let finalUrl = url
      try {
        if (url && typeof url === 'object') {
          if (url.path) finalPath = url.path
          if (url.url || url.downloadUrl) finalUrl = url.url || url.downloadUrl
        }
      } catch {
        /* ignore */
      }
      const savedFileName = (finalPath && finalPath.split('/').pop()) || initialFileName

      // Combine with additional metadata to store in Firestore
      const resourceData = {
        id: resourceId,
        fileId: resourceId,
        fileName: savedFileName,
        originalName: file.name,
        fileType: fileToUpload.type || file.type,
        fileSize: fileToUpload.size,
        url: finalUrl,
        path: finalPath,
        resourceType,
        campaignId,
        permission: permissionObject,
        ...metadata,
        updatedAt: serverTimestamp()
      }

      // Save resource metadata to Firestore for searching and listing
      const resourceRef = await addDoc(
        collection(db, 'campaigns', campaignId, resourceType),
        resourceData
      )

      // Add the Firestore ID to the resource
      const newResource = {
        ...resourceData,
        id: resourceRef.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      // Add to local state
      resources.value.push(newResource)

      return newResource
    } catch (err) {
      console.error(`Error uploading ${resourceType} to campaign ${campaignId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Update a resource's metadata or permissions
   * @param {string} resourceType - Resource type
   * @param {string} campaignId - Campaign ID
   * @param {string} resourceId - Resource ID
   * @param {Object} updates - Fields to update
   * @param {string} [newPermissionLevel] - New permission level
   * @param {Array<string>} [specificUsers] - New specific users list
   * @returns {Promise<Object>} - Updated resource
   */
  async function updateResource(
    resourceType,
    campaignId,
    resourceId,
    updates = {},
    newPermissionLevel,
    specificUsers
  ) {
    if (!resourceType || !campaignId || !resourceId) {
      throw new Error('Resource type, campaign ID, and resource ID are required')
    }

    isLoading.value = true
    error.value = null

    try {
      // Get user ID
      const userId = userStore.userId
      if (!userId) {
        throw new Error('User must be logged in to update resources')
      }

      // Get resource document
      const resourceRef = doc(db, 'campaigns', campaignId, resourceType, resourceId)
      const resourceDoc = await getDoc(resourceRef)

      if (!resourceDoc.exists()) {
        throw new Error('Resource not found')
      }

      const resource = resourceDoc.data()

      // Get campaign to verify permissions
      const campaign = await campaignStore.fetchCampaign(campaignId)
      if (!campaign) {
        throw new Error('Campaign not found')
      }

      // Verify user has permission to update resource
      if (!PermissionService.canWrite(resource.permission, userId, campaign)) {
        throw new Error('You do not have permission to update this resource')
      }

      const updateData = { ...updates }

      // Update permission if needed
      if (newPermissionLevel || specificUsers) {
        // Update permission object
        const updatedPermission = PermissionService.updatePermission(
          resource.permission,
          userId,
          newPermissionLevel,
          specificUsers
        )

        updateData.permission = updatedPermission

        // If permission level changed, need to move the file
        if (newPermissionLevel && newPermissionLevel !== resource.permission.level) {
          // Get current file details (if moving between permission levels you'd handle file move here)
          // Will need to download and re-upload to move between permission levels
          // For now, we'll simply update the metadata - in a real impl, you might want to move the file
        }
      }

      // Add timestamp
      updateData.updatedAt = serverTimestamp()

      // Update in Firestore
      await updateDoc(resourceRef, updateData)

      // Update local state
      const updatedResource = {
        ...resource,
        ...updateData,
        id: resourceId,
        updatedAt: new Date().toISOString() // Client-side timestamp for immediate UI
      }

      const index = resources.value.findIndex((r) => r.id === resourceId)
      if (index !== -1) {
        resources.value[index] = updatedResource
      } else {
        resources.value.push(updatedResource)
      }

      return updatedResource
    } catch (err) {
      console.error(`Error updating ${resourceType} resource ${resourceId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Delete a resource
   * @param {string} resourceType - Resource type
   * @param {string} campaignId - Campaign ID
   * @param {string} resourceId - Resource ID
   * @returns {Promise<boolean>} - Success status
   */
  async function deleteResource(resourceType, campaignId, resourceId) {
    if (!resourceType || !campaignId || !resourceId) {
      throw new Error('Resource type, campaign ID, and resource ID are required')
    }

    isLoading.value = true
    error.value = null

    try {
      // Get user ID
      const userId = userStore.userId
      if (!userId) {
        throw new Error('User must be logged in to delete resources')
      }

      // Get resource document
      const resourceRef = doc(db, 'campaigns', campaignId, resourceType, resourceId)
      const resourceDoc = await getDoc(resourceRef)

      if (!resourceDoc.exists()) {
        throw new Error('Resource not found')
      }

      const resource = resourceDoc.data()

      // Get campaign to verify permissions
      const campaign = await campaignStore.fetchCampaign(campaignId)
      if (!campaign) {
        throw new Error('Campaign not found')
      }

      // Verify user has permission to delete resource
      if (!PermissionService.canWrite(resource.permission, userId, campaign)) {
        throw new Error('You do not have permission to delete this resource')
      }

      // Delete from storage
      await MediaService.deleteCampaignResource(resourceType, campaignId, resource)

      // Delete from Firestore
      await deleteDoc(resourceRef)

      // Update local state
      resources.value = resources.value.filter((r) => r.id !== resourceId)

      // Adjust uploader's storage quota (best-effort)
      try {
        const ownerId = resource?.permission?.createdBy || resource?.createdBy || userId
        let fileSize = resource.fileSize || resource.size || 0

        if (!fileSize || fileSize <= 0) {
          try {
            const candidate =
              resource.path ||
              (resource.url &&
                (typeof resource.url === 'string'
                  ? resource.url
                  : resource.url.path || resource.url.finalUrl || resource.url.fullPath))
            if (candidate) {
              const resolved = await MediaService.getSizeByUrl(candidate)
              if (resolved && Number.isFinite(resolved)) fileSize = Number(resolved)
            }
          } catch (e) {
            console.warn(
              '[campaignResourceStore] failed to resolve file size for deletion tracking',
              e
            )
          }
        }

        if (fileSize > 0 && ownerId) {
          await StorageQuotaService.trackDelete(ownerId, Number(fileSize), {
            type: `campaign-${resourceType}`,
            path: resource.path
          })
        }
      } catch (trackErr) {
        console.error('[campaignResourceStore] Failed to track storage deletion:', trackErr)
        // Do not fail deletion for quota tracking errors
      }
      return true
    } catch (err) {
      console.error(`Error deleting ${resourceType} resource ${resourceId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch resources for a campaign
   * @param {string} resourceType - Resource type to fetch
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Array>} - Array of resources
   */
  async function fetchResources(resourceType, campaignId) {
    if (!resourceType || !campaignId) {
      throw new Error('Resource type and campaign ID are required')
    }

    isLoading.value = true
    error.value = null

    try {
      // Get user ID
      const userId = userStore.userId
      if (!userId) {
        throw new Error('User must be logged in to fetch resources')
      }

      // Get campaign to verify permissions
      const campaign = await campaignStore.fetchCampaign(campaignId)
      if (!campaign) {
        throw new Error('Campaign not found')
      }

      // Query for resources
      const resourcesRef = collection(db, 'campaigns', campaignId, resourceType)
      const resourcesQuery = query(resourcesRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(resourcesQuery)

      // Process results
      const fetchedResources = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }))

      // Filter by permission
      const filteredResources = fetchedResources.filter((resource) =>
        PermissionService.canRead(resource.permission, userId, campaign)
      )

      // Update local state using per-item targeted clones to avoid leaking external refs
      resources.value = [
        ...resources.value.filter(
          (r) => r.resourceType !== resourceType || r.campaignId !== campaignId
        ),
        ...filteredResources.map((it) => targetedCloneForMutation(it))
      ]

      return filteredResources
    } catch (err) {
      console.error(`Error fetching ${resourceType} resources for campaign ${campaignId}:`, err)
      error.value = err.message
      return []
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    resources,
    isLoading,
    error,

    // Getters
    getResourcesByType,
    getResourceById,
    getAccessibleResources,

    // Actions
    uploadResource,
    updateResource,
    deleteResource,
    fetchResources
  }
})
